// ============================================================================
// BESPOKIAN Pharmacist Cockpit - Queue API
// Handles all prescription queue operations
// ============================================================================

import { api, type ApiResponse } from './client';
import type {
  Prescription,
  QueueCounts,
  DURAlert,
  PDMPResult,
  InventoryLot,
} from '@/types';

interface QueueParams {
  status?: string;
  page?: number;
  limit?: number;
}

interface VerificationData {
  verified: boolean;
  checklist: Record<string, boolean>;
}

interface DURReviewData {
  overrides: Record<string, { override: boolean; reason: string }>;
}

interface FillData {
  lotNumber: string;
  expirationDate: string;
  ndcScanned: string;
  quantityFilled: number;
  doubleCountVerified?: boolean;
}

interface PharmacistCheckData {
  approved: boolean;
  notes?: string;
}

export const queueApi = {
  // Queue Operations
  getQueue: (params?: QueueParams): Promise<ApiResponse<Prescription[]>> => {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.set('status', params.status);
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.limit) searchParams.set('limit', String(params.limit));

    return api.get<ApiResponse<Prescription[]>>(
      `/pharmacist/queue?${searchParams.toString()}`
    );
  },

  getQueueCounts: (): Promise<QueueCounts> => {
    return api.get<QueueCounts>('/pharmacist/queue/counts');
  },

  // Prescription Operations
  getPrescription: (id: string): Promise<Prescription> => {
    // Use pharmacist endpoint for cockpit (doesn't check patient ownership)
    return api.get<Prescription>(`/pharmacist/prescription/${id}`);
  },

  getPrescriptionForFill: (id: string): Promise<Prescription> => {
    return api.get<Prescription>(`/prescriptions/${id}/fill`);
  },

  // Verification
  verifyPrescription: (
    id: string,
    data: VerificationData
  ): Promise<Prescription> => {
    return api.post<Prescription>(`/prescriptions/${id}/verify`, data);
  },

  rejectPrescription: (
    id: string,
    reason: string,
    notifyPrescriber?: boolean
  ): Promise<Prescription> => {
    return api.post<Prescription>(`/prescriptions/${id}/reject`, {
      reason,
      notifyPrescriber,
    });
  },

  // DUR Operations
  getDURAlerts: (id: string): Promise<{ rxNumber: string; drugName: string; patientName: string; durAlerts: DURAlert[] }> => {
    return api.get<{ rxNumber: string; drugName: string; patientName: string; durAlerts: DURAlert[] }>(`/prescriptions/${id}/dur`);
  },

  submitDURReview: (id: string, data: DURReviewData): Promise<Prescription> => {
    return api.post<Prescription>(`/prescriptions/${id}/dur/review`, data);
  },

  overrideDUR: (
    prescriptionId: string,
    alertId: string,
    reason: string
  ): Promise<void> => {
    return api.post(`/prescriptions/${prescriptionId}/dur/${alertId}/override`, {
      reason,
    });
  },

  // PDMP Operations
  getPDMPResult: (prescriptionId: string): Promise<PDMPResult> => {
    return api.get<PDMPResult>(`/prescriptions/${prescriptionId}/pdmp`);
  },

  overridePDMP: (prescriptionId: string, reason: string): Promise<void> => {
    return api.post(`/prescriptions/${prescriptionId}/pdmp/override`, { reason });
  },

  // Fill Operations
  getAvailableLots: (ndc: string): Promise<InventoryLot[]> => {
    return api.get<InventoryLot[]>(`/inventory/lots?ndc=${ndc}`);
  },

  fillPrescription: (id: string, data: FillData): Promise<Prescription> => {
    return api.post<Prescription>(`/prescriptions/${id}/fill`, data);
  },

  printLabel: (id: string): Promise<{ jobId: string }> => {
    return api.post<{ jobId: string }>(`/prescriptions/${id}/label/print`);
  },

  // Pharmacist Check
  pharmacistCheck: (
    id: string,
    data: PharmacistCheckData
  ): Promise<Prescription> => {
    return api.post<Prescription>(`/prescriptions/${id}/check`, data);
  },

  // Batch Operations
  batchVerify: (ids: string[]): Promise<{ success: string[]; failed: string[] }> => {
    return api.post<{ success: string[]; failed: string[] }>(
      '/pharmacist/batch/verify',
      { ids }
    );
  },

  batchFill: (
    items: Array<{ prescriptionId: string; lotId: string; quantity: number }>
  ): Promise<{ success: string[]; failed: string[] }> => {
    return api.post<{ success: string[]; failed: string[] }>(
      '/pharmacist/batch/fill',
      { items }
    );
  },

  batchCheck: (ids: string[]): Promise<{ success: string[]; failed: string[] }> => {
    return api.post<{ success: string[]; failed: string[] }>(
      '/pharmacist/batch/check',
      { ids }
    );
  },
};

// Standalone fetch functions for hooks
export async function fetchDURQueue(
  page: number,
  limit: number
): Promise<{ items: Array<{ prescription: Prescription; alerts: DURAlert[] }>; total: number }> {
  return api.get(`/pharmacist/queue/dur?page=${page}&limit=${limit}`);
}

export async function overrideDUR(
  prescriptionId: string,
  alertId: string,
  reason: string
): Promise<void> {
  return queueApi.overrideDUR(prescriptionId, alertId, reason);
}
